import { useState, type ReactNode } from 'react';
import { Box, Collapse, Stack, Typography, styled } from '@mui/material';
import { ChevronDown } from '~/assets/icons';
import { theme } from '~/shared/styles/theme';

export interface Props {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}

const CohortResultsSection = ({ title, icon, children }: Props) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Section>
      <Header
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} flex={1}>
          <IconWrapper>{icon}</IconWrapper>
          <Title>{title}</Title>
        </Stack>
        <ChevronWrapper $expanded={expanded}>
          <ChevronDown width={20} height={20} />
        </ChevronWrapper>
      </Header>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Grid>{children}</Grid>
      </Collapse>
    </Section>
  );
};

export default CohortResultsSection;

const Section = styled(Box)(() => ({
  width: '100%',
  paddingTop: theme.spacing(4),
}));

const Header = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: 63,
  padding: theme.spacing(0, 2),
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.custom.cohortCardBorder}`,
  borderRadius: 10,
  cursor: 'pointer',
}));

const IconWrapper = styled(Stack)(() => ({
  color: theme.palette.custom.darkBrown,
  alignItems: 'center',
  justifyContent: 'center',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

const Title = styled(Typography)(() => ({
  fontSize: 17,
  fontWeight: 700,
  color: theme.palette.custom.darkBrown,
}));

const ChevronWrapper = styled(Stack, {
  shouldForwardProp: (prop) => prop !== '$expanded',
})<{ $expanded?: boolean }>(({ $expanded }) => ({
  color: theme.palette.custom.darkBrown,
  marginRight: theme.spacing(2),
  alignItems: 'center',
  justifyContent: 'center',
  transform: $expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transformOrigin: '50% 50%',
  transition: 'transform 150ms ease',
}));

const Grid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(3),
  marginTop: theme.spacing(4),
}));
